import { FC, useEffect, useState } from "react";
import { toast } from "react-toastify";

import { CheckOutlined, CloseOutlined } from "@ant-design/icons";
import { Button, Input, Space, Tooltip } from "antd";

import { StoredFeatureTemplate } from "../../api/featureTemplate";
import { DEFAULT_TEMPLATE_NAME } from "./defaultTemplate";

interface InlineTemplateRenameProps {
  currentName: string;
  templateIndex: number;
  templates: StoredFeatureTemplate[];
  onSave: (updatedTemplates: StoredFeatureTemplate[]) => void;
  onCancel: () => void;
}

const InlineTemplateRename: FC<InlineTemplateRenameProps> = ({
  currentName,
  templateIndex,
  templates,
  onSave,
  onCancel
}) => {
  const [templateName, setTemplateName] = useState(currentName);
  const [nameError, setNameError] = useState("");

  useEffect(() => {
    setTemplateName(currentName);
    setNameError("");
  }, [currentName]);

  const checkNameValidity = (name: string): string => {
    const trimmedName = name.trim();

    if (!trimmedName) {
      return "Template name cannot be empty";
    }

    if (DEFAULT_TEMPLATE_NAME.toLowerCase() === trimmedName.toLowerCase()) {
      return `Cannot use the reserved name "${DEFAULT_TEMPLATE_NAME}"`;
    }

    const existingTemplate = templates.find(
      (t, i) => i !== templateIndex && t.name.toLowerCase() === trimmedName.toLowerCase()
    );
    if (existingTemplate) {
      return `A template named "${trimmedName}" already exists`;
    }

    return "";
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setTemplateName(value);
    const error = checkNameValidity(value);
    setNameError(error);
  };

  const handleSave = () => {
    const error = checkNameValidity(templateName);
    if (error) {
      setNameError(error);
      return;
    }

    const trimmedName = templateName.trim();
    const updatedTemplates = templates.map((t, i) =>
      i === templateIndex ? { ...t, name: trimmedName } : t
    );
    onSave(updatedTemplates);
    toast.success("Template renamed successfully");
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    e.stopPropagation();
    if (e.key === "Enter") {
      handleSave();
    } else if (e.key === "Escape") {
      onCancel();
    }
  };

  return (
    <>
      <div
        style={{ flex: 1, position: "relative", marginBottom: nameError ? "22px" : "0" }}>
        <Input
          value={templateName}
          onChange={handleInputChange}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={handleKeyDown}
          status={nameError ? "error" : ""}
          autoFocus
          style={{ width: "100%", height: "26px" }}
        />
        {nameError && (
          <div
            style={{
              color: "#ff4d4f",
              fontSize: "12px",
              marginTop: "2px",
              position: "absolute",
              left: 0,
              right: 0
            }}>
            {nameError}
          </div>
        )}
      </div>
      <Space size="small" style={{ marginTop: "2px", alignSelf: "flex-start" }}>
        <Tooltip title="Save">
          <Button
            type="primary"
            size="small"
            icon={<CheckOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              handleSave();
            }}
            disabled={!!nameError}
          />
        </Tooltip>
        <Tooltip title="Cancel">
          <Button
            size="small"
            icon={<CloseOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              onCancel();
            }}
          />
        </Tooltip>
      </Space>
    </>
  );
};

export default InlineTemplateRename;
